import { createContext, useContext, useState, useEffect } from "react";
import { portalLogin } from "../api/services";

const SESSION_KEY        = "iz_portal_session";
const SESSION_DURATION_MS = 6 * 60 * 60 * 1000;

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);

  // On mount — restore session from sessionStorage if still valid
  useEffect(() => {
    const raw = sessionStorage.getItem(SESSION_KEY);
    if (raw) {
      try {
        const parsed = JSON.parse(raw);
        if (parsed.expiresAt && Date.now() < parsed.expiresAt) {
          setSession(parsed);
        } else {
          sessionStorage.removeItem(SESSION_KEY);
        }
      } catch {
        sessionStorage.removeItem(SESSION_KEY);
      }
    }
    setLoading(false);
  }, []);

  // Poll every 30s — auto-expire the session if time is up
  useEffect(() => {
    if (!session) return;
    const interval = setInterval(() => {
      if (Date.now() >= session.expiresAt) {
        logout({ expired: true });
      }
    }, 30000);
    return () => clearInterval(interval);
  }, [session]);

  // Updated login function - only Employee ID, no password
  async function login(employeeId) {
    const id = employeeId.trim().toUpperCase();
    if (!id) return false;

    try {
      // Call the backend API with only employeeId
      const response = await portalLogin({ employeeId: id });
      
      console.log("API Response:", response);
      if (response.valid === true) {
        const existingRaw = sessionStorage.getItem(SESSION_KEY);
        let savedName = null;
        let isReturning = false;
        
        if (existingRaw) {
          try {
            const existing = JSON.parse(existingRaw);
            if (existing.employeeId === id && existing.name) {
              savedName = existing.name;
              isReturning = true;
            }
          } catch {}
        }

        // Also check the name store
        const nameStoreRaw = sessionStorage.getItem("iz_name_store");
        if (!savedName && nameStoreRaw) {
          try {
            const nameStore = JSON.parse(nameStoreRaw);
            if (nameStore[id]) {
              savedName = nameStore[id].name;
              isReturning = nameStore[id].visitCount > 0;
            }
          } catch {}
        }

        // If no saved name, use the username from API response
        if (!savedName && response.username) {
          savedName = response.username;
        }

        const newSession = {
          employeeId: id,
          token: response.token || `temp_token_${Date.now()}`,
          username: response.username,
          loginAt: Date.now(),
          expiresAt: Date.now() + SESSION_DURATION_MS,
          name: savedName,
          isReturning: isReturning,
          userData: response
        };

        sessionStorage.setItem(SESSION_KEY, JSON.stringify(newSession));
        setSession(newSession);
        return true;
      } else {
        console.error("Login failed:", response.message);
        return false;
      }
    } catch (error) {
      console.error("Login API error:", error);
      throw error;
    }
  }

  function saveName(name) {
    if (!session) return;
    const trimmed = name.trim();
    if (!trimmed) return;

    const updated = { ...session, name: trimmed };
    sessionStorage.setItem(SESSION_KEY, JSON.stringify(updated));
    setSession(updated);

    let nameStore = {};
    try {
      nameStore = JSON.parse(sessionStorage.getItem("iz_name_store") || "{}");
    } catch {}
    nameStore[session.employeeId] = {
      name: trimmed,
      visitCount: (nameStore[session.employeeId]?.visitCount || 0) + 1,
    };
    sessionStorage.setItem("iz_name_store", JSON.stringify(nameStore));
  }

  function recordVisit() {
    if (!session) return;
    let nameStore = {};
    try {
      nameStore = JSON.parse(sessionStorage.getItem("iz_name_store") || "{}");
    } catch {}
    const existing = nameStore[session.employeeId] || { visitCount: 0 };
    nameStore[session.employeeId] = {
      ...existing,
      visitCount: existing.visitCount + 1,
    };
    sessionStorage.setItem("iz_name_store", JSON.stringify(nameStore));

    if (existing.visitCount >= 1) {
      const updated = { ...session, isReturning: true };
      sessionStorage.setItem(SESSION_KEY, JSON.stringify(updated));
      setSession(updated);
    }
  }

  function logout({ expired = false } = {}) {
    sessionStorage.removeItem(SESSION_KEY);
    setSession(null);
    if (expired) {
      window.location.href = "https://izsoftwares.com";
    }
  }

  const isAuthenticated = !!session;

  return (
    <AuthContext.Provider
      value={{
        session,
        isAuthenticated,
        loading,
        login,
        logout,
        saveName,
        recordVisit,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside <AuthProvider>");
  return ctx;
}